import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import ImageWithLoader from "shared/ui/image";
import { fetchProducts } from "features/products/api/productsApi";
import { createBonusRule } from "../api/discountsApi";
import CustomLoader from "shared/ui/loader";
import { toast } from "react-toastify";

export default function BonusProductModal({
  show,
  handleClose,
  product = {},
  onSuccess,
}) {
  const mainProductId = product?.id;

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedMap, setSelectedMap] = useState({});

  // 🔐 Нууц үг
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  // 🆕 Аль хэдийн бонусд байгаа бараануудын ID-ууд (bonusProductId)
  const [preselectedIds, setPreselectedIds] = useState([]);

  const load = async (pageToLoad = 1, isRefresh = false) => {
    try {
      setLoading(true);
      const res = await fetchProducts({
        page: pageToLoad,
        categoryId: null,
        keyword: search,
      });

      const rows = Array.isArray(res?.data) ? res.data : [];
      if (isRefresh) setList(rows);
      else setList((prev) => [...prev, ...rows]);

      setHasMore(res?.pagination?.currentPage < res?.pagination?.totalPages);
      setPage(res?.pagination?.currentPage || pageToLoad);
    } catch (e) {
      console.error("fetch bonus products error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Модал нээгдэх болгонд state-уудыг шинэчилнэ
  useEffect(() => {
    if (show) {
      setSearch("");
      setSelectedIds(new Set());
      setSelectedMap({});
      setPassword("");
      setList([]);

      // 🆕 product.bonusItems → preselectedIds болгож авна
      const bonusItems = Array.isArray(product?.bonusItems)
        ? product.bonusItems
        : [];
      const ids = bonusItems
        .map((b) => b.bonusProductId)
        .filter((id) => !!id);
      setPreselectedIds(ids);

      load(1, true);
    }
  }, [show, product?.id]);

  // Хайлт өөрчлөгдөхөд барааны жагсаалтыг дахин ачаална
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      setList([]);
      load(1, true);
    }, 300);
    return () => clearTimeout(t);
  }, [search, show]);

  // preselectedIds → selectedIds. ⚠️ `list`-д ХАМААРУУЛАХГҮЙ — өмнө нь list
  // өөрчлөгдөх бүрт (хуудаслахад) энэ дахин ажиллаж, хэрэглэгчийн ШИНЭ сонголтыг
  // preselected руу БУЦААДАГ байв.
  useEffect(() => {
    if (!show) return;
    setSelectedIds(new Set(preselectedIds));
  }, [show, preselectedIds]);

  // list ачаалагдах бүрт сонгогдсон барааны ДЭЛГЭРЭНГҮЙГ (нэр/зураг) НЭМНЭ —
  // сонголтыг дахин тохируулахгүй (weekly-best-products.js загвар). Хадгалахдаа
  // selectedIds-ийг ашигладаг тул ачаалаагүй хуудасны preselected ч алдагдахгүй.
  useEffect(() => {
    if (!list.length) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      list.forEach((it) => {
        if (selectedIds.has(it.id) && !next[it.id]) {
          next[it.id] = { id: it.id, name: it.name, image: it.image, price: it.price };
        }
      });
      return next;
    });
  }, [list, selectedIds]);

  const toggleSelect = (id, obj) => {
    if (id === mainProductId) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

    setSelectedMap((prev) => {
      const next = { ...prev };
      next[id] ? delete next[id] : (next[id] = obj);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedMap({});
    setPreselectedIds([]); // урьдчилсан сонголтыг ч хоосолчихно
  };

  const selectedArr = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const canSave = useMemo(
    () => selectedIds.size > 0 && !saving && password,
    [selectedIds, saving, password]
  );

  const buildPayload = () => ({
    ruleName: `${product?.name} → Бонус`,
    ruleType: "PRODUCT_QTY",
    conditionProductId: mainProductId,
    minQuantity: 1,
    startAt: null,
    endAt: null,
    isActive: 1,
    // ⭐ selectedIds-ээс (selectedMap БИШ) — ачаалаагүй хуудасны preselected бараа
    //    хадгалахад алдагдахгүй (өмнө нь selectedMap зөвхөн ачаалсан хуудсаар бөглөгддөг).
    bonusProducts: [...selectedIds].map((id) => ({
      productId: id,
      quantity: 1,
    })),
    password: password,
  });

  const handleSave = async () => {
    if (!password) {
      toast.error("Нууц үгээ оруулна уу.");
      return;
    }

    if (selectedIds.size === 0) {
      toast.error("Бонус бараа сонгоогүй байна.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildPayload();
      const res = await createBonusRule(payload);

      if (res?.success) {
        toast.success("Урамшуулал амжилттай нэмэгдлээ.");
        clearSelection();
        handleClose();
        onSuccess?.();
      } else {
        toast.error(res?.message || "Урамшуулал нэмэхэд алдаа гарлаа.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Серверийн алдаа гарлаа.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Урамшуулал нэмэх</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-3">
          <Col xs={12} className="d-flex align-items-center">
            <ImageWithLoader
              src={product?.image}
              alt={product?.name}
              width="60px"
              height="60px"
              className="me-3 rounded"
            />
            <div>
              <div>
                <strong>{product?.name}</strong>
              </div>
              <div className="text-muted">
                Үндсэн бараа (ID: {product?.id})
              </div>
            </div>
          </Col>
        </Row>

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Урамшуулалд нэмэх бараа хайх..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="outline-secondary"
            onClick={() => {
              setList([]);
              load(1, true);
            }}
          >
            Хайх
          </Button>
        </InputGroup>

        {/* Сонгогдсон бараанууд */}
        <div className="d-flex justify-content-between mb-3">
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: 8 }}
          >
            {selectedArr.length ? (
              selectedArr.map((it) => (
                <div
                  key={it.id}
                  className="d-flex align-items-center"
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    padding: "4px 8px",
                  }}
                >
                  <ImageWithLoader
                    src={it.image}
                    width="26px"
                    height="26px"
                    className="me-2 rounded"
                  />
                  <span style={{ maxWidth: 140 }} className="text-truncate">
                    {it.name}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-muted">Сонгогдсон бараа алга</span>
            )}
          </div>

          <div>
            <Badge bg="secondary">Нийт: {selectedArr.length}</Badge>
            <Button
              className="ms-2"
              variant="light"
              size="sm"
              disabled={selectedArr.length === 0}
              onClick={clearSelection}
            >
              Цэвэрлэх
            </Button>
          </div>
        </div>

        {/* Бараануудын жагсаалт */}
        <div
          style={{
            maxHeight: 420,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: 6,
          }}
        >
          {loading ? (
            <div className="text-center p-3">
              <CustomLoader fontSize={26} />
            </div>
          ) : (
            <ListGroup variant="flush">
              {list.map((it) => {
                const disabled = it.id === mainProductId;
                const checked = selectedIds.has(it.id);

                return (
                  <ListGroup.Item
                    key={it.id}
                    className="d-flex align-items-center"
                  >
                    <Form.Check
                      className="me-3"
                      type="checkbox"
                      disabled={disabled}
                      checked={checked}
                      onChange={() => toggleSelect(it.id, it)}
                    />

                    <ImageWithLoader
                      src={it.image}
                      width="40px"
                      height="40px"
                      className="me-3 rounded"
                    />

                    <div className="flex-grow-1">
                      <strong>{it.name}</strong>
                      <div className="text-muted">
                        {Number(it.price ?? 0).toLocaleString()}₮
                      </div>
                    </div>

                    {disabled && (
                      <span className="badge bg-secondary">Үндсэн</span>
                    )}
                  </ListGroup.Item>
                );
              })}

              {hasMore && (
                <ListGroup.Item className="text-center">
                  <Button
                    variant="light"
                    disabled={loading}
                    onClick={() => load(page + 1)}
                  >
                    Дараагийнх
                  </Button>
                </ListGroup.Item>
              )}

              {!loading && list.length === 0 && (
                <ListGroup.Item className="text-center text-muted">
                  Илэрц олдсонгүй
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </div>

        {/* 🔐 Password Section */}
        <Form.Group className="mt-3">
          <Form.Label>Нууц үг</Form.Label>
          <InputGroup>
            <Form.Control
              type={showPw ? "text" : "password"}
              placeholder="Хадгалахын тулд нууц үгээ оруулна уу"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              variant="outline-secondary"
              onClick={() => setShowPw(!showPw)}
            >
              {showPw ? (
                <i className="ri-eye-off-line" />
              ) : (
                <i className="ri-eye-line" />
              )}
            </Button>
          </InputGroup>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Хаах
        </Button>
        <Button variant="danger" onClick={handleSave} disabled={!canSave}>
          {saving ? "Хадгалж байна…" : "Хадгалах"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
